/**
 * Player settings store.
 * Manages player-editable settings with persistence to localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_CAMERA_CONFIG, CameraConfig } from '../config/settings/camera';

/**
 * Player settings interface.
 * Only includes settings with PLAYER visibility.
 */
export interface PlayerSettings {
  camera: Pick<CameraConfig, 'defaultZoom' | 'panSpeed' | 'edgeScrollZone' | 'edgeScrollSpeed'>;
  // Future: audio, graphics, controls, etc.
}

/**
 * Get default player settings.
 */
function getDefaultPlayerSettings(): PlayerSettings {
  return {
    camera: {
      defaultZoom: DEFAULT_CAMERA_CONFIG.defaultZoom,
      panSpeed: DEFAULT_CAMERA_CONFIG.panSpeed,
      edgeScrollZone: DEFAULT_CAMERA_CONFIG.edgeScrollZone,
      edgeScrollSpeed: DEFAULT_CAMERA_CONFIG.edgeScrollSpeed,
    },
  };
}

interface SettingsState {
  settings: PlayerSettings;
  updateCameraSettings: (settings: Partial<PlayerSettings['camera']>) => void;
  resetSettings: () => void;
  resetCameraSettings: () => void;
}

/**
 * Player settings store with localStorage persistence.
 */
export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      settings: getDefaultPlayerSettings(),
      updateCameraSettings: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            camera: { ...state.settings.camera, ...updates },
          },
        })),
      resetSettings: () => set({ settings: getDefaultPlayerSettings() }),
      resetCameraSettings: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            camera: {
              defaultZoom: DEFAULT_CAMERA_CONFIG.defaultZoom,
              panSpeed: DEFAULT_CAMERA_CONFIG.panSpeed,
              edgeScrollZone: DEFAULT_CAMERA_CONFIG.edgeScrollZone,
              edgeScrollSpeed: DEFAULT_CAMERA_CONFIG.edgeScrollSpeed,
            },
          },
        })),
    }),
    {
      name: 'castle-survivors-settings',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
